import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  expandItemsForRooms,
  newId,
  useCapturePhoto,
  useOutbox,
  type ChecklistRun,
} from '@dia/db';
import { useT } from '@dia/i18n';
import { Button, PhotoCapture } from '@dia/ui';
import { RequireAuth, type Identity } from './lib/identity';
import { Icon } from './lib/icons';
import { loadItems, loadJobCatalog, loadRun, loadRunItems, useLoad, useReferencePhoto } from './lib/data';
import {
  deriveStates,
  emptyState,
  isComplete,
  needsPhoto,
  nextCard,
  orderCards,
  PROBLEM_NO,
  PROBLEM_YES,
  progress,
  runPhase,
  type Card as CardModel,
  type CardState,
} from './lib/checklist';
import { sendMaintenanceRequest } from './lib/requests';
import { Field, inputClass, Notice, Page, Pill } from './lib/ui';

/**
 * One cleaning job, room by room. The order comes from lib/checklist.ts, so it
 * is the same on every phone: damage first, laundry next, mop last house wide.
 *
 * Every tick goes to the outbox and the screen believes it straight away. A
 * photo is resized and queued the moment it is taken, and the row that owns it
 * carries its path, so the tick never waits for the upload.
 */
export function LimpiezaRun() {
  return <RequireAuth>{(who) => <RunBody who={who} />}</RequireAuth>;
}

function RunBody({ who }: { who: Identity }) {
  const { runId = '' } = useParams();
  const { t, lang } = useT();
  const { enqueue, pending } = useOutbox();
  const key = `${runId}:${pending}`;

  const run = useLoad(() => loadRun(runId), `run:${key}`, 30_000);
  const catalog = useLoad(loadJobCatalog, 'catalog');
  const items = useLoad(() => (run.data ? loadItems(run.data.checklist_id) : Promise.resolve([])), `items:${run.data?.checklist_id ?? ''}`);
  const rows = useLoad(() => loadRunItems(runId), `rows:${key}`, 30_000);

  const parts = useMemo(() => ({ runId, stayId: run.data?.stay_id ?? null }), [runId, run.data?.stay_id]);
  const capture = useCapturePhoto({ bucket: 'run-photos', parts });

  const houseRooms = useMemo(
    () => (catalog.data?.rooms ?? []).filter((r) => r.house_id === run.data?.house_id),
    [catalog.data?.rooms, run.data?.house_id],
  );
  const cards = useMemo(() => {
    const pairs = expandItemsForRooms(items.data ?? [], houseRooms).filter(
      (pair) => !pair.item.room_id || pair.room !== null,
    );
    return orderCards(pairs);
  }, [items.data, houseRooms]);
  const states = useMemo(() => deriveStates(rows.data ?? []), [rows.data]);
  const prog = progress(cards, states);
  const upNext = nextCard(cards, states);

  if (run.loading) return <Page title={t('ops.nav.cleaning')} back="/limpieza"><p>{t('loading')}</p></Page>;
  if (!run.data) {
    return (
      <Page title={t('ops.nav.cleaning')} back="/limpieza">
        <Notice tone="warn">{t('ops.cleaning.not_found')}</Notice>
      </Page>
    );
  }

  const job: ChecklistRun = run.data;
  const phase = runPhase(job);
  const canEdit = phase === 'working' || phase === 'redo';
  const houseName = catalog.data?.houses.find((h) => h.id === job.house_id);
  const listName = catalog.data?.checklists.find((c) => c.id === job.checklist_id);

  const rowPayload = (card: CardModel, extra: Record<string, unknown> = {}) => ({
    run_id: runId,
    item_id: card.item.id,
    room_id: card.room?.id ?? null,
    done_at: new Date().toISOString(),
    created_by: who.staffId,
    ...extra,
  });

  const after = async () => {
    await Promise.all([rows.reload(), run.reload()]);
  };

  const tick = async (card: CardModel, note: string | null = null) => {
    await enqueue({ table: 'run_items', payload: rowPayload(card, note ? { note } : {}) });
    await after();
  };

  const photo = async (card: CardModel, file: File): Promise<string> => {
    const { path } = await capture(file, { itemId: card.item.id });
    await enqueue({ table: 'run_items', payload: rowPayload(card, { photo_path: path }) });
    await after();
    return path;
  };

  /** A damage photo is queued on its own. The row that owns it is written when the report is sent. */
  const damagePhoto = async (card: CardModel, file: File): Promise<string> => {
    const { path } = await capture(file, { itemId: card.item.id });
    return path;
  };

  /** Yes, something is wrong here: the damage photo, the note, and a request to maintenance. */
  const reportProblem = async (card: CardModel, note: string, path: string) => {
    await enqueue({ table: 'run_items', payload: rowPayload(card, { note: PROBLEM_YES, photo_path: path }) });
    await sendMaintenanceRequest({
      enqueue,
      requestId: newId(),
      staffId: who.staffId,
      token: who.token,
      lang,
      target: { roomEs: card.room?.name_es ?? null, roomEn: card.room?.name_en ?? null },
      note,
      assetId: null,
      roomId: card.room?.id ?? null,
      photoPaths: [`run-photos:${path}`],
      voiceNotePath: null,
      priority: 3,
    });
    await after();
  };

  const finish = async () => {
    await enqueue({
      table: 'checklist_runs',
      kind: 'update',
      payload: { completed_at: new Date().toISOString() },
      match: { client_id: job.client_id },
    });
    await after();
  };

  const reviewNote = lang === 'es' ? job.review_note_es : job.review_note_en;
  let lastGroup = '';

  return (
    <Page title={houseName ? (lang === 'es' ? houseName.name_es : houseName.name_en) : t('ops.nav.cleaning')} back="/limpieza">
      <p className="mb-3 text-inkMuted">{listName ? (lang === 'es' ? listName.name_es : listName.name_en) : ''}</p>

      <div className="mb-4">
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="font-medium">{t('ops.cleaning.progress', { done: prog.done, total: prog.total })}</span>
          <Pill tone={phase === 'redo' ? 'danger' : phase === 'approved' ? 'ok' : phase === 'waiting' ? 'info' : 'warn'}>
            {t(`ops.phase.${phase}`)}
          </Pill>
        </div>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={prog.total}
          aria-valuenow={prog.done}
          aria-label={t('ops.cleaning.progress', { done: prog.done, total: prog.total })}
          className="h-4 w-full overflow-hidden rounded bg-surfaceDeep"
        >
          <div className="h-full bg-accent" style={{ width: `${prog.total ? (prog.done / prog.total) * 100 : 0}%` }} />
        </div>
        <p className="mt-2 text-inkMuted">{t('ops.cleaning.order_note')}</p>
      </div>

      {phase === 'redo' && (
        <div className="mb-4 flex flex-col gap-2">
          <Notice tone="danger">
            <strong>{t('ops.cleaning.redo')}</strong>
            <br />
            {reviewNote ?? job.review_note_es ?? job.review_note_en ?? ''}
          </Notice>
          <p>{t('ops.cleaning.redo_help')}</p>
        </div>
      )}
      {phase === 'waiting' && <Notice tone="info">{t('ops.cleaning.waiting')}</Notice>}
      {phase === 'approved' && (
        <div className="mb-4 flex flex-col gap-2">
          <Notice tone="ok">{t('ops.cleaning.approved')}</Notice>
          {reviewNote && (
            <p>
              <strong>{t('ops.cleaning.review_note')}:</strong> {reviewNote}
            </p>
          )}
        </div>
      )}

      {cards.length === 0 && <Notice tone="warn">{t('ops.cleaning.no_catalog')}</Notice>}

      <div className="flex flex-col gap-3">
        {cards.map((card) => {
          const group = `${card.stage}:${card.stage === 'room' ? card.room?.id : ''}`;
          const heading =
            group === lastGroup
              ? null
              : card.stage === 'open'
                ? t('ops.cleaning.stage_open')
                : card.stage === 'mop'
                  ? t('ops.cleaning.stage_mop')
                  : card.stage === 'close'
                    ? t('ops.cleaning.stage_close')
                    : card.room
                      ? lang === 'es'
                        ? card.room.name_es
                        : card.room.name_en
                      : t('ops.cleaning.house_wide');
          lastGroup = group;
          return (
            <div key={card.key} className="flex flex-col gap-3">
              {heading && <h2 className="mt-3 border-b border-line pb-1 text-[1.2em] font-semibold">{heading}</h2>}
              <CardView
                card={card}
                state={states.get(card.key) ?? emptyState()}
                isNext={upNext?.key === card.key}
                canEdit={canEdit}
                redo={phase === 'redo'}
                onTick={() => tick(card)}
                onNo={() => tick(card, PROBLEM_NO)}
                onPhoto={(file) => photo(card, file)}
                onDamagePhoto={(file) => damagePhoto(card, file)}
                onProblem={(note, path) => reportProblem(card, note, path)}
              />
            </div>
          );
        })}
      </div>

      {canEdit && cards.length > 0 && (
        <div className="mt-6 flex flex-col gap-2">
          {prog.open > 0 && <Notice tone="warn">{t('ops.cleaning.finish_blocked_cards', { count: prog.open })}</Notice>}
          {prog.missingPhotos > 0 && (
            <Notice tone="warn">{t('ops.cleaning.finish_blocked_photos', { count: prog.missingPhotos })}</Notice>
          )}
          <Button size="lg" block disabled={!prog.finished} onClick={() => void finish()}>
            <Icon name="check" /> {phase === 'redo' ? t('ops.cleaning.finish_again') : t('ops.cleaning.finish')}
          </Button>
        </div>
      )}
    </Page>
  );
}

function CardView({
  card,
  state,
  isNext,
  canEdit,
  redo,
  onTick,
  onNo,
  onPhoto,
  onDamagePhoto,
  onProblem,
}: {
  card: CardModel;
  state: CardState;
  isNext: boolean;
  canEdit: boolean;
  redo: boolean;
  onTick: () => Promise<void>;
  onNo: () => Promise<void>;
  onPhoto: (file: File) => Promise<string>;
  onDamagePhoto: (file: File) => Promise<string>;
  onProblem: (note: string, path: string) => Promise<void>;
}) {
  const { t, lang } = useT();
  const { item } = card;
  const reference = useReferencePhoto(item.reference_photo_path);
  const [busy, setBusy] = useState(false);
  const [askingYes, setAskingYes] = useState(false);
  const [damagePath, setDamagePath] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState(false);

  const text = lang === 'es' ? item.text_es : item.text_en;
  const hint = lang === 'es' ? item.hint_es : item.hint_en;
  const complete = isComplete(card, state);
  const wantsPhoto = needsPhoto(card, state);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  // A finished card folds down to one line. A redo lets the reviewer's crew reopen it.
  if (complete && !(redo && canEdit && !item.ask_problem)) {
    return (
      <div className="flex items-start gap-3 rounded border border-line bg-surface p-3 opacity-90">
        <span className="mt-1 text-ok">
          <Icon name="check" />
        </span>
        <div className="flex-1">
          <p className="m-0">{item.ask_problem ? (state.problem ? t('ops.cleaning.problem_sent') : t('ops.cleaning.problem_none')) : text}</p>
        </div>
        <Pill tone="ok">{t('ops.cleaning.done_word')}</Pill>
      </div>
    );
  }

  return (
    <div className={`rounded border-2 bg-surface p-4 ${isNext ? 'border-accent' : 'border-line'}`}>
      {isNext && <p className="mb-1 font-semibold text-accent">{t('ops.cleaning.next')}</p>}
      <p className="m-0 text-[1.1em] font-medium">{item.ask_problem ? t('ops.cleaning.problem_question') : text}</p>
      {hint && !item.ask_problem && <p className="mt-1 text-inkMuted">{hint}</p>}
      {wantsPhoto && !item.ask_problem && <p className="mt-1 font-medium text-warn">{t('ops.cleaning.photo_required')}</p>}

      {reference && (
        <figure className="m-0 mt-3">
          <img src={reference} alt="" className="max-h-64 w-full rounded border border-line object-cover" />
          <figcaption className="mt-1 text-inkMuted">{t('ops.cleaning.reference_photo')}</figcaption>
        </figure>
      )}

      {canEdit && !sent && (
        <div className="mt-3 flex flex-col gap-3">
          {item.ask_problem ? (
            askingYes ? (
              <div className="flex flex-col gap-3">
                <p className="m-0 font-medium">{t('ops.cleaning.problem_photo')}</p>
                <PhotoCapture
                  max={1}
                  onCapture={(p) => void run(async () => setDamagePath(await onDamagePhoto(p.file)))}
                />
                <Field label={t('ops.cleaning.problem_note')}>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={inputClass} />
                </Field>
                <Button
                  size="lg"
                  block
                  disabled={!damagePath}
                  busy={busy}
                  busyLabel={t('loading')}
                  onClick={() =>
                    damagePath &&
                    void run(async () => {
                      await onProblem(note, damagePath);
                      setSent(true);
                    })
                  }
                >
                  <Icon name="send" /> {t('action.send')}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Button size="lg" variant="secondary" disabled={busy} onClick={() => void run(onNo)}>
                  {t('ops.cleaning.problem_no')}
                </Button>
                <Button size="lg" variant="danger" onClick={() => setAskingYes(true)}>
                  {t('ops.cleaning.problem_yes')}
                </Button>
              </div>
            )
          ) : (
            <>
              {!state.done && (
                <Button size="lg" block disabled={busy} onClick={() => void run(onTick)}>
                  <Icon name="check" /> {t('ops.cleaning.done_button')}
                </Button>
              )}
              {(wantsPhoto || redo) && (
                <PhotoCapture
                  max={2}
                  label={redo && state.done ? t('ops.cleaning.repeat') : t('action.take_photo')}
                  onCapture={(p) => void run(async () => void (await onPhoto(p.file)))}
                />
              )}
              {state.done && wantsPhoto && !state.photoPath && (
                <p className="m-0 font-medium text-warn">{t('ops.cleaning.photo_missing')}</p>
              )}
              {state.photoPath && <p className="m-0 text-ok">{t('ops.cleaning.photo_saved')}</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
